/**
 * FirstPartyAnalyticsFunnel — admin panel that visualizes the new
 * privacy-safe, stealth-aware funnel.
 *
 * Source-of-truth: GET /api/admin/analytics/funnel
 *
 * Sections:
 *  1. 8-stage funnel (Visitors → Sample Routine → Selfie Upload →
 *     Routine Generated → Signup Started → Signup Completed →
 *     Checkout Started → Subscription Completed)
 *  2. Stealth vs Non-Stealth side-by-side funnel
 *  3. Device breakdown (mobile / tablet / desktop)
 *  4. Locale breakdown (en / fr / es / pt)
 *  5. Stealth share of total sessions
 *  6. Daily trend (mini sparkline of distinct sessions)
 *
 * Uses ONLY data tagged `source: "first_party_backend"` on the
 * backend, so this widget never double-counts with the legacy
 * /api/events funnel rendered elsewhere on the dashboard.
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { TrendingDown, Eye, EyeOff, Smartphone, Globe2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmt = (n) => (typeof n === "number" ? n.toLocaleString() : "—");
const fmtPct = (v) => (v === null || v === undefined ? "—" : `${v}%`);

// Same 8-color ramp as legacy AdminAnalytics for visual continuity.
const STEP_COLORS = [
  "#D4B483", "#C4A373", "#B49363", "#A48353",
  "#9B6A6C", "#8B5A5C", "#7B4A4C", "#6B3A3C",
];

const FunnelStep = ({ label, users, peak, stepPct, dropOff, color }) => {
  const widthPct = peak > 0 ? Math.round((users / peak) * 1000) / 10 : 0;
  return (
    <div
      className="space-y-1.5"
      data-testid={`fp-funnel-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-white font-medium">{label}</span>
        <span className="text-white/55 tabular-nums text-xs">
          {fmt(users)}
          {stepPct !== null && stepPct !== undefined && (
            <span className="ml-2 text-white/35">step {fmtPct(stepPct)}</span>
          )}
          {dropOff !== null && dropOff !== undefined && dropOff > 0 && (
            <span className="ml-2 text-[#F4B8C0]">
              <TrendingDown className="inline h-3 w-3 -mt-0.5" /> {fmt(dropOff)}
            </span>
          )}
        </span>
      </div>
      <div className="h-2.5 w-full bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${widthPct}%`, background: color }}
        />
      </div>
    </div>
  );
};

const BreakdownBar = ({ label, value, pct, icon }) => (
  <div className="flex items-center gap-3" data-testid={`fp-breakdown-${label}`}>
    <div className="w-7 h-7 rounded-md bg-white/[0.05] border border-white/10 flex items-center justify-center text-white/60">
      {icon}
    </div>
    <div className="flex-1">
      <div className="flex items-baseline justify-between text-xs mb-1">
        <span className="text-white/85 capitalize">{label}</span>
        <span className="text-white/45 tabular-nums">
          {fmt(value)} · {fmtPct(pct)}
        </span>
      </div>
      <div className="h-1.5 w-full bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className="h-full bg-[#D4B483] rounded-full transition-all duration-500"
          style={{ width: `${pct || 0}%` }}
        />
      </div>
    </div>
  </div>
);

const Spark = ({ data }) => {
  // Tiny sparkline of `distinct_sessions` per day. No axes — this is a
  // glanceable trend, not a precision chart. Day list is already sorted
  // chronologically by the backend.
  const sessions = data.map((d) => d.distinct_sessions || 0);
  const max = Math.max(1, ...sessions);
  const points = sessions
    .map((s, i) => `${(i / Math.max(1, sessions.length - 1)) * 100},${100 - (s / max) * 100}`)
    .join(" ");
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      className="w-full h-16"
      data-testid="fp-daily-spark"
    >
      <polyline
        points={points}
        fill="none"
        stroke="#D4B483"
        strokeWidth="2"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};

const Panel = ({ title, meta, children, testid }) => (
  <div
    className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 sm:p-7 mb-6"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-lg font-medium text-white">{title}</h2>
      {meta && (
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          {meta}
        </span>
      )}
    </div>
    {children}
  </div>
);

export default function FirstPartyAnalyticsFunnel({ days = 30 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/admin/analytics/funnel?days=${days}`, { withCredentials: true })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.status || "error");
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [days]);

  const peakFunnel = useMemo(() => {
    if (!data?.funnel?.length) return 0;
    return data.funnel.reduce((m, s) => Math.max(m, s.users || 0), 0);
  }, [data]);

  if (loading) {
    return (
      <Panel title="First-party funnel" meta="loading" testid="fp-funnel-loading">
        <div className="text-white/40 text-sm">Loading first-party analytics…</div>
      </Panel>
    );
  }
  if (error) {
    return (
      <Panel title="First-party funnel" meta={`error ${error}`} testid="fp-funnel-error">
        <div className="text-[#F4B8C0] text-sm">
          Couldn't load the first-party funnel ({error}).
        </div>
      </Panel>
    );
  }
  if (!data) return null;

  const peakStealth = data.stealth_split?.stealth?.reduce(
    (m, s) => Math.max(m, s.users || 0), 0,
  ) || 0;
  const peakNonStealth = data.stealth_split?.non_stealth?.reduce(
    (m, s) => Math.max(m, s.users || 0), 0,
  ) || 0;

  return (
    <>
      <Panel
        title="First-party funnel · privacy-safe, stealth-aware"
        meta={`${days}d · source: first_party_backend`}
        testid="fp-funnel-main"
      >
        <p className="text-xs text-white/45 mb-4 max-w-2xl">
          Tracks every session — including Stealth Mode users — without
          storing IP, full UA, userId, email, raw selfies, ad IDs, or
          full referrer URLs. Stealth events carry a {' '}
          <code className="text-[#D4B483]">stealth_mode=true</code> flag
          so we can measure privacy-first product usage.
        </p>
        <div className="space-y-4">
          {data.funnel?.map((step, idx) => (
            <FunnelStep
              key={step.event}
              label={step.label}
              users={step.users}
              peak={peakFunnel}
              stepPct={step.step_pct}
              dropOff={step.drop_off}
              color={STEP_COLORS[idx % STEP_COLORS.length]}
            />
          ))}
        </div>
      </Panel>

      <Panel
        title="Stealth vs Non-Stealth funnel"
        meta="side-by-side"
        testid="fp-funnel-stealth-split"
      >
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.22em] text-[#D4B483]">
              <EyeOff className="h-3.5 w-3.5" /> Stealth Mode
            </div>
            <div className="space-y-3">
              {data.stealth_split?.stealth?.map((s, i) => (
                <FunnelStep
                  key={`stealth-${s.event}`}
                  label={s.label}
                  users={s.users}
                  peak={peakStealth || 1}
                  stepPct={s.step_pct}
                  dropOff={s.drop_off}
                  color={STEP_COLORS[i % STEP_COLORS.length]}
                />
              ))}
            </div>
          </div>
          <div>
            <div className="flex items-center gap-2 mb-3 text-xs uppercase tracking-[0.22em] text-[#D4B483]">
              <Eye className="h-3.5 w-3.5" /> Non-Stealth
            </div>
            <div className="space-y-3">
              {data.stealth_split?.non_stealth?.map((s, i) => (
                <FunnelStep
                  key={`non-stealth-${s.event}`}
                  label={s.label}
                  users={s.users}
                  peak={peakNonStealth || 1}
                  stepPct={s.step_pct}
                  dropOff={s.drop_off}
                  color={STEP_COLORS[i % STEP_COLORS.length]}
                />
              ))}
            </div>
          </div>
        </div>
        <div className="mt-5 pt-5 border-t border-white/10 grid grid-cols-3 gap-4 text-center">
          <div data-testid="fp-stealth-pct">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4B483] mb-1">
              Stealth share
            </div>
            <div className="text-2xl text-white tabular-nums font-light">
              {fmtPct(data.stealth_share?.stealth_pct)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4B483] mb-1">
              Stealth sessions
            </div>
            <div className="text-2xl text-white tabular-nums font-light">
              {fmt(data.stealth_share?.stealth_sessions)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4B483] mb-1">
              Non-stealth sessions
            </div>
            <div className="text-2xl text-white tabular-nums font-light">
              {fmt(data.stealth_share?.non_stealth_sessions)}
            </div>
          </div>
        </div>
      </Panel>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <Panel title="Device breakdown" meta="distinct sessions" testid="fp-device">
          <div className="space-y-3">
            {data.device_breakdown?.length ? (
              data.device_breakdown.map((d) => (
                <BreakdownBar
                  key={d.device_type}
                  label={d.device_type}
                  value={d.sessions}
                  pct={d.pct}
                  icon={<Smartphone className="h-3.5 w-3.5" />}
                />
              ))
            ) : (
              <div className="text-white/35 text-sm">No data yet.</div>
            )}
          </div>
        </Panel>
        <Panel title="Locale breakdown" meta="distinct sessions" testid="fp-locale">
          <div className="space-y-3">
            {data.locale_breakdown?.length ? (
              data.locale_breakdown.map((d) => (
                <BreakdownBar
                  key={d.locale}
                  label={d.locale}
                  value={d.sessions}
                  pct={d.pct}
                  icon={<Globe2 className="h-3.5 w-3.5" />}
                />
              ))
            ) : (
              <div className="text-white/35 text-sm">No data yet.</div>
            )}
          </div>
        </Panel>
      </div>

      <Panel title="Daily trend" meta="distinct sessions / day" testid="fp-trend">
        {data.daily_trend?.length ? (
          <>
            <Spark data={data.daily_trend} />
            <div className="flex justify-between text-[10px] text-white/40 mt-2 tabular-nums">
              <span>{data.daily_trend[0]?.day}</span>
              <span>
                peak: {fmt(Math.max(...data.daily_trend.map((d) => d.distinct_sessions || 0)))}
              </span>
              <span>{data.daily_trend[data.daily_trend.length - 1]?.day}</span>
            </div>
          </>
        ) : (
          <div className="text-white/35 text-sm">No data in this window.</div>
        )}
      </Panel>
    </>
  );
}
